import React, { useEffect, useState } from "react";
import UITransition from "../../ui/Transition";
import { CustomStyleProvider } from "../../utils/CustomStyleProvider";
import { Alert, Breadcrumb, Col, Collapse, Popconfirm, Row, Skeleton, Slider, Tag } from "antd";
import { UITitle } from "../../ui/Title";
import { BaseColor } from "../../../theme/maps/Colors";
import { ClockCircleOutlined, CopyOutlined, DeleteOutlined, EditOutlined, FilePdfOutlined, PlusOutlined, SettingOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { selectorCustomStyleOptions } from "../../../redux-modules/selectors/selectorsUI";
import { UICard } from "../../ui/Card";
import { UIText } from "../../ui/Text";
import styled from "styled-components";
import { UILink } from "../../ui/Link";
import { Routes } from "../../../utils/Routes";
import { useParams } from "react-router";
import apiClient from "../../../utils/apiClient";
import { API_ENDPOINTS } from "../../../constants/api";
import { showNotification } from "../../../utils/showNotification";
import {
  TTemporaryShift,
  TTemporarySubshift,
  TTemporarySubshiftCreateRequest,
  TTemporarySubshiftUpdateRequest,
  TUserTemporarySubshift,
} from "../../../redux-modules/declarations/maps/shifts";
import { UITable } from "../../ui/Table";

import { UIInput } from "../../ui/Input";
import moment from "moment";
import { UIButton } from "../../ui/Button";


import { getUsersSubshiftListColumns } from "./usersSubShiftsListColumns";
import { DrawerCreateSubshift } from "./drawer_subshift";

const { Panel } = Collapse;

export const ViewTurniOccasionaliEdit = (): JSX.Element => {
    //@ts-ignore
    const { id } = useParams();

    const [showCustomStyleOptions, setShowCustomStyleOptions] = useState(false);
    const customStyleOptions = useSelector(selectorCustomStyleOptions);
    const [zoomLevel, setZoomLevel] = useState(customStyleOptions["ShiftsListZoomLevel"]);

    const [eventDetail, setEventDetail] = useState<TTemporaryShift>();
    const [eventDetailLoading, setEventDetailLoading] = useState(false);

    const [showDrawerSubshift, setShowDrawerSubshift] = useState(false);
    const [editingSubshift, setEditingSubshift] = useState<TTemporarySubshift | undefined>(undefined);
    const [isSubshiftSaving, setIsSubshiftSaving] = useState(false);
    const [isExportingPdf, setIsExportingPdf] = useState(false);

    useEffect(() => {
        getEvent();
    }, []);

    async function getEvent(){
        setEventDetailLoading(true)
        try{

            const response = await apiClient.get(API_ENDPOINTS.TEMPORARY_SHIFTS_DETAIL.replace(":event_id", id))

            if(response.status == 200){
                const result: TTemporaryShift = response.data;
                setEventDetail(result)
            }
        }catch(err){
            // Caso in cui err ha la response della chiamata perché l'api non ha restituito 200
            // @ts-ignore
            if(err.response){
                // @ts-ignore
                const response: AxiosResponse = err.response;

                if(response.data){
                    const info = response.data;
                    showNotification("error", "Turno Occasionale", info.message);
                }
            }else{
                showNotification("error", "Turno Occasionale", "Si è verificato un imprevisto al caricamento del turno, riprovare.");
            }
        }finally{
            setEventDetailLoading(false)
        }
    }

    async function handleRemoveUser(subshift: TTemporarySubshift, user: TUserTemporarySubshift){
        try{
            const body = {
                user_id: user.id,
                temporary_subshift_id: subshift.id
            };

            const response = await apiClient.post(API_ENDPOINTS.TEMPORARY_SHIFTS_DELETE_USER, body);

            if(response.status == 200){
                showNotification("success", "Rimozione utente dall'evento", response.data.message);

                setEventDetail(prev => {
                    if (!prev) return prev;

                    return {
                        ...prev,
                        subshifts: prev.subshifts?.map(s => {
                            if (s.id !== subshift.id) return s;

                            return {
                                ...s,
                                joined_users: user.is_latecomer ? s.joined_users : s.joined_users - 1,
                                users: s.users?.filter(u => u.id !== user.id),
                            };
                        }),
                    };
                });
            }
        }catch(err){
            // Caso in cui err ha la response della chiamata perché l'api non ha restituito 200
            // @ts-ignore
            if(err.response){
                // @ts-ignore
                const response: AxiosResponse = err.response;

                if(response.data){
                    const info = response.data;
                    showNotification("error", "Oops..", info.message);
                }
            }else{
                showNotification("error", "Oops..", "Si è verificato un imprevisto, riprovare.");
            }
        }
    }

    const handleOpenAddSubshift = () => {
        setEditingSubshift(undefined);
        setShowDrawerSubshift(true);
    };

    const handleOpenEditSubshift = (subshift: TTemporarySubshift) => {
        setEditingSubshift(subshift);
        setShowDrawerSubshift(true);
    };

    const handleCloseDrawerSubshift = () => {
        setShowDrawerSubshift(false);
        setEditingSubshift(undefined);
    };

    const handleSaveSubshift = async (info: TTemporarySubshiftCreateRequest | TTemporarySubshiftUpdateRequest) => {
        setIsSubshiftSaving(true);
        try{
            const isEdit = "temporary_subshift_id" in info;

            const response = isEdit
                ? await apiClient.post(API_ENDPOINTS.TEMPORARY_SUBSHIFTS_UPDATE, info)
                : await apiClient.post(API_ENDPOINTS.TEMPORARY_SUBSHIFTS, info);

            if(response.status == 200){
                showNotification("success", "Sede", response.data.message);
                setShowDrawerSubshift(false);
                setEditingSubshift(undefined);
                getEvent();
            }
        }catch(err){
            // @ts-ignore
            if(err.response){
                // @ts-ignore
                const response: AxiosResponse = err.response;

                if(response.data){
                    const info2 = response.data;
                    showNotification("error", "Sede", info2.message);
                }
            }else{
                showNotification("error", "Sede", "Si è verificato un imprevisto, riprovare.");
            }
        }finally{
            setIsSubshiftSaving(false);
        }
    };

    const handleDeleteSubshift = async (subshift: TTemporarySubshift) => {
        try{
            const response = await apiClient.post(API_ENDPOINTS.TEMPORARY_SUBSHIFTS_DELETE, {
                temporary_subshift_id: subshift.id,
            });

            if(response.status == 200){
                showNotification("success", "Sede", response.data.message);
                setEventDetail(prev => {
                    if (!prev) return prev;

                    return {
                        ...prev,
                        subshifts: prev.subshifts?.filter(s => s.id !== subshift.id),
                    };
                });
            }
        }catch(err){
            // @ts-ignore
            if(err.response){
                // @ts-ignore
                const response: AxiosResponse = err.response;

                if(response.data){
                    const info = response.data;
                    showNotification("error", "Sede", info.message);
                }
            }else{
                showNotification("error", "Sede", "Si è verificato un imprevisto, riprovare.");
            }
        }
    };

    const copyLink = (link?: string) => {
        if (link) {
            navigator.clipboard.writeText(link);
            showNotification("success", "Link", "Link copiato negli appunti!");
        }
    };

    const handleExportPdf = async () => {
        setIsExportingPdf(true);
        try {
            const response = await apiClient.get(
                API_ENDPOINTS.TEMPORARY_SHIFTS_EXPORT_PDF.replace(":event_id", id),
                { responseType: "blob" }
            );

            const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `evento_${eventDetail?.name ?? id}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            // La response è un blob anche in caso di errore: provo a leggerlo come JSON per il messaggio,
            // altrimenti mostro un errore generico.
            let message = "Si è verificato un imprevisto durante l'esportazione del PDF, riprovare.";
            try {
                // @ts-ignore
                const response: AxiosResponse = err.response;
                if (response?.data) {
                    const text = await (response.data as Blob).text();
                    const parsed = JSON.parse(text);
                    if (parsed?.message) message = parsed.message;
                }
            } catch (parseErr) {
                // ignoro: uso il messaggio generico già impostato
            }
            showNotification("error", "Esportazione PDF", message);
        } finally {
            setIsExportingPdf(false);
        }
    };

    return (
        <>
            <UITransition>
                <CustomStyleProvider section="Shifts">
                <Row justify="space-between" align="middle" gutter={[16, 16]}>
                    <Col style={{ display: "inline-flex", alignItems: "baseline" }}>
                    <UITitle level={2} color={BaseColor}>
                        Turni Occasionali
                    </UITitle>
                        <span
                                style={{
                                fontSize: "18px",
                                color: BaseColor,
                                cursor: "pointer",
                                marginLeft: "10px",
                                position: "relative",
                                top: "-3px",
                            }}
                            onClick={() => setShowCustomStyleOptions(!showCustomStyleOptions)}
                        >
                            <SettingOutlined />
                        </span>
                    </Col>
                </Row>

                <Row>
                    <Breadcrumb>
                        <Breadcrumb.Item>
                            <UILink route={Routes.turniOccasionali} label={"Turni Occasionali"} />
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>
                            {eventDetail?.name}
                        </Breadcrumb.Item>
                    </Breadcrumb>
                </Row>

                {showCustomStyleOptions && (
                    <UITransition>
                    <Row gutter={[8, 8]} style={{ marginBottom: "10px" }}>
                        <Col span={24}>
                        <UICard>
                            <Row gutter={[8, 8]}>
                            <Col>
                                <UITitle level={5} color={BaseColor}>
                                Personalizza pagina
                                </UITitle>
                            </Col>
                            </Row>
                            <Row gutter={[8, 8]}>
                            <Col span={8}>
                                <UIText>Livello di zoom</UIText>
                                <Slider
                                    min={0.7}
                                    max={1}
                                    onChange={(value: number) => setZoomLevel(value)}
                                    value={typeof zoomLevel === "number" ? zoomLevel : 0}
                                    step={0.1}
                                    tooltipVisible={false}
                                    marks={{
                                        0.7: "70%",
                                        0.8: "80%",
                                        0.9: "90%",
                                        1: "100%",
                                    }}
                                />
                            </Col>
                            </Row>
                        </UICard>
                        </Col>
                    </Row>
                    </UITransition>
                )}

                {eventDetailLoading ?
                    <UICard style={{ marginBottom: "20px" }}>
                        <Skeleton active />
                    </UICard>

                    :

                    <>
                        <CustomCard>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <label>Nome</label>
                                    <UIInput readOnly={true} value={eventDetail?.name}/>
                                </Col>
                                <Col span={8}>
                                    <label>Luogo</label>
                                    <UIInput readOnly={true} value={eventDetail?.place}/>
                                </Col>
                                <Col span={8}>
                                    <label>Data Evento</label>
                                    <UIInput readOnly={true} value={eventDetail?.date ? moment(eventDetail.date).format("DD/MM/YYYY") : ""}/>
                                </Col>
                            </Row>

                            <Row style={{ marginTop: 16 }} align="bottom">
                                <Col span={16}>
                                    <label>Link Generico Evento</label>
                                    <UIInput readOnly={true} value={eventDetail?.link_to_share}/>
                                </Col>
                                <Col>
                                    <UIButton
                                        icon={<CopyOutlined />}
                                        onClick={() => copyLink(eventDetail?.link_to_share)}
                                        style={{marginLeft: "8px"}}
                                    >
                                        Copia
                                    </UIButton>
                                </Col>
                            </Row>

                            <Alert
                                style={{ marginTop: 12 }}
                                type="warning"
                                showIcon
                                message={
                                    <>
                                        Al momento della candidatura, il sistema individua automaticamente la sede
                                        predefinita del dipendente e la confronta con le sedi create per questo
                                        evento. Se per la sua sede esiste un solo turno, la candidatura va subito
                                        a buon fine e gli vengono mostrati gli orari di quella sede; se ne esistono
                                        più di uno, gli viene chiesto di scegliere a quale candidarsi. Se invece
                                        per la sua sede <strong>non</strong> è stata creata alcuna sede/turno in
                                        questo evento, il dipendente non potrà candidarsi tramite questo link.
                                    </>
                                }
                            />
                        </CustomCard>

                        <Row justify="end" style={{ marginTop: 16, marginBottom: 8 }}>
                            <UIButton style={{marginRight: "8px"}} icon={<FilePdfOutlined />} onClick={handleExportPdf} loading={isExportingPdf}>
                                Esporta PDF
                            </UIButton>
                            <UIButton type="primary" icon={<PlusOutlined />} onClick={handleOpenAddSubshift}>
                                Aggiungi Sede
                            </UIButton>
                        </Row>

                        <StyledCollapse>
                            {[...(eventDetail?.subshifts ?? [])]
                                .sort((a, b) => a.office.name.localeCompare(b.office.name, "it"))
                                .map(subshift => {
                                const registeredUsers = subshift.users?.filter(u => u.is_latecomer === 0) ?? [];
                                const latecomerUsers = subshift.users?.filter(u => u.is_latecomer === 1) ?? [];
                                const hasEnrolled = (subshift.users?.length ?? 0) > 0;

                                return (
                                    <Panel
                                        key={subshift.id}
                                        header={
                                            <span>
                                                <strong>{subshift.office.name}</strong>
                                                {" — "}
                                                {registeredUsers.length}/{subshift.max_users} posti occupati
                                            </span>
                                        }
                                        extra={
                                            <span onClick={e => e.stopPropagation()}>
                                                <EditOutlined
                                                    style={{ marginRight: 16, cursor: "pointer" }}
                                                    onClick={() => handleOpenEditSubshift(subshift)}
                                                />
                                                <Popconfirm
                                                    title={hasEnrolled
                                                        ? "Non puoi eliminare una sede con utenti già iscritti."
                                                        : "Vuoi eliminare questa sede?"}
                                                    okText="Elimina"
                                                    cancelText="Annulla"
                                                    okButtonProps={{ danger: true, disabled: hasEnrolled }}
                                                    onConfirm={() => !hasEnrolled && handleDeleteSubshift(subshift)}
                                                >
                                                    <DeleteOutlined
                                                        style={{
                                                            cursor: hasEnrolled ? "not-allowed" : "pointer",
                                                            opacity: hasEnrolled ? 0.4 : 1,
                                                        }}
                                                    />
                                                </Popconfirm>
                                            </span>
                                        }
                                    >
                                        <Row gutter={16}>
                                            <Col span={6}>
                                                <label>Posti Totali</label>
                                                <UIInput readOnly={true} value={subshift.max_users}/>
                                            </Col>

                                            <Col span={6}>
                                                <label>Dipendenti Iscritti</label>
                                                <UIInput readOnly={true} value={subshift.joined_users}/>
                                            </Col>
                                        </Row>

                                        <Row style={{ marginTop: 16 }}>
                                            <Col span={24}>
                                                <label>Turni</label>
                                                <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 8 }}>
                                                    {subshift.shift_types.map(shift => (
                                                        <Tag
                                                            key={shift.id}
                                                            color="blue"
                                                            icon={<ClockCircleOutlined />}
                                                            style={{ fontSize: 13, padding: "5px 12px", borderRadius: 6 }}
                                                        >
                                                            {shift.name}
                                                            {shift.is_notturno ? " (Notturno)" : ""}
                                                        </Tag>
                                                    ))}
                                                </div>
                                            </Col>
                                        </Row>

                                        <Row style={{ marginTop: 16 }} align="bottom">
                                            <Col span={16}>
                                                <label>Link Sede</label>
                                                <UIInput readOnly={true} value={subshift.link_to_share}/>
                                            </Col>
                                            <Col>
                                                <UIButton
                                                    icon={<CopyOutlined />}
                                                    onClick={() => copyLink(subshift.link_to_share)}
                                                    style={{marginLeft: "8px"}}
                                                >
                                                    Copia
                                                </UIButton>
                                            </Col>
                                        </Row>

                                        <Alert
                                            style={{ marginTop: 12 }}
                                            type="warning"
                                            showIcon
                                            message={
                                                <>
                                                    A differenza del link generico, con questo link il dipendente si
                                                    candida direttamente a <strong>questa</strong> sede: non è
                                                    richiesto che la sua sede predefinita corrisponda a{" "}
                                                    <strong>{subshift.office.name}</strong>, e vede da subito gli
                                                    orari indicati sopra, senza dover scegliere tra più opzioni.
                                                    Utile per invitare esplicitamente un gruppo di dipendenti
                                                    (anche di sedi diverse) a candidarsi proprio a questa sede.
                                                </>
                                            }
                                        />

                                        <Row gutter={[16,16]} style={{ marginTop: 24 }}>
                                            <Col span={24}>
                                                <UITable
                                                    title={() => <UIText strong>DIPENDENTI ISCRITTI {registeredUsers.length +"/"+ subshift.max_users}</UIText>}
                                                    size="middle"
                                                    data-cy="subshift-users-table"
                                                    columns={getUsersSubshiftListColumns(user => handleRemoveUser(subshift, user), true)}
                                                    dataSource={registeredUsers}
                                                />
                                            </Col>
                                        </Row>

                                        <Row gutter={[16,16]} style={{ marginTop: 16 }}>
                                            <Col span={24}>
                                                <UITable
                                                    title={() => <UIText strong>DIPENDENTI RITARDATARI</UIText>}
                                                    size="middle"
                                                    data-cy="subshift-latecomers-table"
                                                    columns={getUsersSubshiftListColumns(user => handleRemoveUser(subshift, user), false)}
                                                    dataSource={latecomerUsers}
                                                />
                                            </Col>
                                        </Row>
                                    </Panel>
                                );
                            })}
                        </StyledCollapse>
                    </>
                }

                </CustomStyleProvider>
            </UITransition>

            <DrawerCreateSubshift
                visible={showDrawerSubshift}
                onClose={handleCloseDrawerSubshift}
                onSave={handleSaveSubshift}
                isLoading={isSubshiftSaving}
                temporaryShiftId={id}
                editingSubshift={editingSubshift}
            />
        </>
    )
}

const CustomCard = styled(UICard)`
  .ant-card-body {
    padding: 10px;
  }
`;

const StyledCollapse = styled(Collapse)`
  background: transparent;
  border: none;

  .ant-collapse-item {
    margin-bottom: 14px;
    border: 1px solid #f0f0f0;
    border-radius: 8px !important;
    overflow: hidden;
    background: #fff;
  }

  .ant-collapse-item:last-child {
    margin-bottom: 0;
  }

  .ant-collapse-header {
    border-radius: 8px 8px 0 0 !important;
  }

  .ant-collapse-content {
    border-radius: 0 0 8px 8px !important;
  }

  .ant-collapse-content-box {
    border-radius: 0 0 8px 8px;
  }
`;